/**
 * @file Users.jsx
 * @description Users component for displaying and managing users.
 * @component Users
 * @exports Users - Main component for displaying and managing users.
 */

import React, { useState, useEffect, useMemo } from "react";
import Grid from "@mui/material/Grid2";
import { Add } from "@mui/icons-material";
import { TextField, Button, Container, Alert } from "@mui/material";
import { getComparator, handleSort } from "../../../hooks/useTable.js";
import { getUserRoles } from "../../../services/adminModule/roles/roleServices.js";
import { getUsers, addUser, editUser, deleteUser } from "../../../services/adminModule/users/userServices.js";
import { getAgentIdentity } from "../../../services/ivrModule/acsServices.js";
import { ROLE_ERRORS } from "../../../common/constants/adminModule/roleConstants.js";
import { AUTO_COMPLETE, VARIANTS, BUTTON_COLORS, STATUS, SORT_ORDERS, SIZES } from "../../../common/constants/commonConstants.js";
import { USER_STRINGS, USER_KEYS, USER_MESSAGES, USER_ERRORS, USER_TABLE_COLUMNS } from "../../../common/constants/adminModule/userConstants.js";
import UsersTable from "./UsersTable.jsx";
import DeleteUserDialog from "./DeleteUserDialog.jsx";
import AddEditUserDialog from "./AddEditUserDialog.jsx";
import ProgressOverlay from "../../../common/components/ProgressOverlay.jsx";

const formatUserName = (name) => name ? name.charAt(0).toUpperCase() + name.slice(1).toLowerCase() : "";

const Users = () => {
    const [page, setPage] = useState(0);
    const [users, setUsers] = useState([]);
    const [userRoles, setUserRoles] = useState([]);
    const [isSaving, setIsSaving] = useState(false);
    const [searchUser, setSearchUser] = useState("");
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [orderBy, setOrderBy] = useState(USER_KEYS.NAME);
    const [userToDelete, setUserToDelete] = useState(null);
    const [selectedUser, setSelectedUser] = useState(null);
    const [loadingUsers, setLoadingUsers] = useState(true);
    const [loadingRoles, setLoadingRoles] = useState(true);
    const [successMessage, setSuccessMessage] = useState("");
    const [order, setOrder] = useState(SORT_ORDERS.ASCENDING);
    const [isAddEditUserModalOpen, setIsAddEditUserModalOpen] = useState(false);
    const [isConfirmDeleteUserModal, setIsConfirmDeleteUserModal] = useState(false);
    const loading = loadingUsers || loadingRoles;

    useEffect(() => { loadUsers(); fetchRoles(); }, []);

    const loadUsers = async () => {
        try {
            setLoadingUsers(true);
            setUsers(await getUsers());
        }
        catch (error) {
            console.error(USER_ERRORS.FETCH_USERS_ERROR, error);
        }
        finally {
            setLoadingUsers(false);
        }
    };

    const fetchRoles = async () => {
        try {
            setLoadingRoles(true);
            setUserRoles(await getUserRoles());
        }
        catch (error) {
            console.error(ROLE_ERRORS.FETCH_ROLES_ERROR, error);
        }
        finally {
            setLoadingRoles(false);
        }
    };

    const usersList = users.map((user) => ({
        ...user,
        name: `${user.firstname ?? ""} ${user.lastname ?? ""}`.trim(),
    }));

    const { usersToDisplay, filteredUsersLength } = useMemo(() => {
        const filteredUsers = usersList.filter((user) =>
            user.name.toLowerCase().includes(searchUser.toLowerCase())
        );
        return {
            usersToDisplay: filteredUsers
                .sort(getComparator(order, orderBy))
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
            filteredUsersLength: filteredUsers.length,
        };
    }, [usersList, searchUser, order, orderBy, page, rowsPerPage]);

    const handleAddEditUserClick = (user = {}) => {
        setSelectedUser(user);
        setIsAddEditUserModalOpen(true);
    };

    const handleSaveUser = async () => {
        if (!selectedUser) return;
        let acsInfo = null;
        let formattedExpiresOn = null;
        let workerid = selectedUser.workerid || null;
        if (!selectedUser.id) {
            acsInfo = await getAgentIdentity();
            if (!acsInfo || !acsInfo.userId || !acsInfo.token) {
                console.warn("ACS Identity not available. Failed to save user.");
                return;
            }
            const expiresOn = new Date(acsInfo.expiresOn);
            formattedExpiresOn = `${expiresOn.getFullYear()}-${String(expiresOn.getMonth() + 1).padStart(2, "0")}-${String(
                expiresOn.getDate()
            ).padStart(2, "0")} ${String(expiresOn.getHours()).padStart(2, "0")}:${String(
                expiresOn.getMinutes()
            ).padStart(2, "0")}:${String(expiresOn.getSeconds()).padStart(2, "0")}.${String(
                expiresOn.getMilliseconds()
            ).padStart(3, "0")}`;
            const acsIdTokenPart = acsInfo.userId?.slice(-4);
            const userLastName = selectedUser.lastname
                ? selectedUser.lastname.toLowerCase().replace(/\s+/g, "")
                : "user";
            workerid = `wkr-${userLastName}-${acsIdTokenPart}`;
        }
        const userToSave = {
            ...selectedUser,
            firstname: formatUserName(selectedUser.firstname),
            lastname: formatUserName(selectedUser.lastname),
            emailaddress: selectedUser.emailaddress?.toLowerCase() || "",
            workerid: workerid,
            ACSIdentity: acsInfo?.userId || selectedUser.ACSIdentity || null,
            ACSIdentityToken: acsInfo?.token || selectedUser.ACSIdentityToken || null,
            ACSIdentityExpiration: formattedExpiresOn || selectedUser.ACSIdentityExpiration || null,
        };

        try {
            setIsSaving(true);
            setIsAddEditUserModalOpen(false);
            if (selectedUser.id) {
                await editUser(userToSave);
                setSuccessMessage(USER_MESSAGES.USER_UPDATED);
            } else {
                await addUser(userToSave);
                setSuccessMessage(USER_MESSAGES.USER_ADDED);
            }
            await loadUsers();
            setTimeout(() => setSuccessMessage(""), 3000);
        } catch (error) {
            console.error(USER_ERRORS.SAVE_USER_ERROR, error);
        } finally {
            setIsSaving(false);
        }
    };

    const handleDeleteUserClick = (userId) => {
        setUserToDelete(userId);
        setIsConfirmDeleteUserModal(true);
    };

    const handleConfirmDeleteUser = async () => {
        if (!userToDelete) return;
        try {
            await deleteUser(userToDelete);
            setSuccessMessage(USER_MESSAGES.USER_DELETED);
            setIsConfirmDeleteUserModal(false);
            setUserToDelete(null);
            await loadUsers();
        } catch (error) { console.error(USER_ERRORS.DELETE_USER_ERROR, error); }
    };

    const handleInputChange = (field) => (e) =>
        setSelectedUser({ ...selectedUser, [field]: e.target.value });

    const handleRequestSort = (property) => {
        handleSort(order, orderBy, property, setOrder, setOrderBy);
    };

    return (
        <>
            {isSaving && (
				<>
					<ProgressOverlay />
				</>
			)}
            <Container width="xl" disableGutters>
                {
                    successMessage && 
                    <Alert severity={STATUS.SUCCESS} sx={{ mb: 2, justifyContent: "center" }}>
                        {successMessage}
                    </Alert>
                }
                <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid size={SIZES.GROW}>
                        <TextField
                            fullWidth
                            size={SIZES.SMALL}
                            value={searchUser}
                            variant={VARIANTS.OUTLINED}
                            autoComplete={AUTO_COMPLETE.OFF}
                            label= {USER_STRINGS.SEARCH_USERS}
                            onChange={e => setSearchUser(e.target.value)}
                        />
                    </Grid>
                    <Grid size={SIZES.AUTO}>
                        <Button
                            startIcon={<Add />}
                            variant={VARIANTS.CONTAINED}
                            color={BUTTON_COLORS.PRIMARY}
                            onClick={() => handleAddEditUserClick({})}
                        >
                            {USER_STRINGS.ADD_USER}
                        </Button>
                    </Grid>
                </Grid>
                <UsersTable
                    page={page}
                    order={order}
                    setPage={setPage}
                    orderBy={orderBy}
                    isLoading={loading}
                    loadUsers={loadUsers}
                    users={usersToDisplay}
                    rowsPerPage={rowsPerPage}
                    columns={USER_TABLE_COLUMNS}
                    onEdit={handleAddEditUserClick}
                    setRowsPerPage={setRowsPerPage}
                    onDelete={handleDeleteUserClick}
                    handleRequestSort={handleRequestSort}
                    filteredUsersLength={filteredUsersLength}
                />
                <AddEditUserDialog
                    userRoles={userRoles}
                    onSave={handleSaveUser}
                    selectedUser={selectedUser}
                    open={isAddEditUserModalOpen}
                    handleInputChange={handleInputChange}
                    onClose={() => setIsAddEditUserModalOpen(false)}
                />
                <DeleteUserDialog
                    open={isConfirmDeleteUserModal}
                    onConfirm={handleConfirmDeleteUser}
                    onClose={() => setIsConfirmDeleteUserModal(false)}
                />
            </Container>
        </>
    )
};

export default Users;